import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Input,
  Button,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { StyledForm } from "./Form.styled";
import { ChangeEvent, useContext, useMemo, useState } from "react";
import { LanguageContext } from "../../globals/LanguageContext";
import { Invoice, InvoiceDto } from "../../models/Invoice";
import { Controller, useForm } from "react-hook-form";
import {
  formatDateToDDMMYYYYRaw,
  formatDateToYYYYMMDD,
  formatPrice,
} from "../../globals/utils";
import { Upload } from "@phosphor-icons/react";
import { useProject } from "../../stores/useProject";
import { useInvoice } from "../../stores/useInvoice";
import { useProfile } from "../../stores/useProfile";
import { updateInvoice } from "../../repositories/InvoiceRepository";

type AddInvoiceFormProps = {
  invoice?: Invoice;
  onSuccess: () => void;
};

type InvoiceFormValues = {
  type: "varia" | "inkomst" | "uitgave" | "uitbetaling";
  invoiceDate: string;
  paymentDate?: string;
  name: string;
  description: string;
  projectId?: number;
  priceExVat: number;
  vat: number;
  totalAmount: number;
  quarter: string;
  hasBeenPayed: boolean;
  file?: File;
};

export const AddInvoiceForm = ({ invoice, onSuccess }: AddInvoiceFormProps) => {
  const [fileCount, setFileCount] = useState<number>(0);
  const [previewFiles, setPreviewFiles] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const { dictionary } = useContext(LanguageContext);
  const { projects } = useProject();
  const { addInvoice, addFileToInvoice, invoices } = useInvoice();
  const { user } = useProfile();
  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { errors },
  } = useForm<InvoiceFormValues>({
    defaultValues: {
      priceExVat: invoice?.priceExVat ? invoice?.priceExVat : 0,
      vat: invoice?.vat ? invoice?.vat : 0,
      invoiceDate: invoice?.invoiceDate
        ? formatDateToYYYYMMDD(new Date(invoice.invoiceDate))
        : "",
      paymentDate: invoice?.paymentDate
        ? formatDateToYYYYMMDD(invoice.paymentDate)
        : "",
      description: invoice?.description ? invoice.description : "",
      type: invoice?.type ? invoice.type : "uitgave",
      projectId: invoice?.project?.id ? invoice?.project.id : undefined,
      hasBeenPayed: invoice?.hasBeenPayed ? invoice.hasBeenPayed : false,
    },
  });

  const priceExVat = watch("priceExVat") ?? invoice?.priceExVat;
  const vat = watch("vat") ?? invoice?.vat;

  const totalAmount = useMemo(() => {
    return (Number(priceExVat) + Number(vat)).toFixed(2);
  }, [priceExVat, vat]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    setPreviewFiles([]);
    setSelectedFiles([]);
    setFileCount(0);

    if (file) {
      setSelectedFiles([file]);
      setSelectedFileName(file.name);
      setFileCount(1);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setPreviewFiles([base64String.split(",")[1]]);
      };
      reader.readAsDataURL(file);
    }
  };

  const previousDescriptions = useMemo(() => {
    if (!invoices) return [];
    const uniqueDescriptions = Array.from(
      new Set(invoices.map((inv) => inv.description))
    );
    return uniqueDescriptions;
  }, [invoices]);

  const onSubmit = async (data: InvoiceFormValues) => {
    if (!user) return;
    try {
      const formData = new FormData();

      const invoiceData: InvoiceDto = {
        ...data,
        invoiceDate: new Date(data.invoiceDate),
        paymentDate: data.paymentDate ? new Date(data.paymentDate) : undefined,
        totalAmount: Number(data.priceExVat) + Number(data.vat),
        quarter: `${new Date(data.invoiceDate).getFullYear()}-Q${
          Math.floor(new Date(data.invoiceDate).getMonth() / 3) + 1
        }`,
        name:
          selectedFiles.length > 0
            ? selectedFileName.split(".")[0]
            : invoice
            ? invoice.name
            : "",
      };

      if (invoice && invoice.id) {
        const status = await updateInvoice(user, invoiceData, invoice.id);
        if (status === 200 || status === 204) {
          if (selectedFiles.length > 0) {
            await addFileToInvoice(user, selectedFiles[0], invoice.id);
          }
          return onSuccess();
        }
      } else {
        const newInvoice = await addInvoice(user, invoiceData);

        if (!newInvoice || !newInvoice.id) return;

        if (selectedFiles.length > 0) {
          const fileUploadResponse = await addFileToInvoice(
            user,
            selectedFiles[0],
            newInvoice.id
          );
          if (fileUploadResponse === 201) {
            return onSuccess();
          }
        } else {
          return onSuccess();
        }
      }
    } catch (error) {
      console.error("Submit error:", error);
    }
  };

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">
            {dictionary.invoiceform.invoiceDate}
          </Typography>
          <TextField
            type="date"
            {...register("invoiceDate", {
              required: dictionary.errormessages.form_fieldrequired,
            })}
            error={!!errors.invoiceDate}
            helperText={errors?.invoiceDate?.message}
            onBlur={(e) => formatDateToYYYYMMDD(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
        <Box className="column">
          <Typography variant="h4">
            {dictionary.invoiceform.paymentDate}
          </Typography>
          <TextField
            type="date"
            {...register("paymentDate")}
            helperText={errors?.paymentDate?.message}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
        <Box className="column">
          <Typography variant="h4">
            {dictionary.invoiceform.invoiceupload}
          </Typography>
          <Box className="fileInput">
            <Input
              type="file"
              inputProps={{ multiple: false, accept: "application/pdf" }}
              onChange={handleFileChange}
            />
            <Typography className="input-label">
              {selectedFileName || dictionary.invoiceform.chooseinvoice}
              <Upload size={32} />
            </Typography>
          </Box>
        </Box>
        <Box className="column">
          <Typography variant="h4">
            {dictionary.invoiceform.invoicePayed}
          </Typography>
          <Controller
            name="hasBeenPayed"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={""}
              />
            )}
          />
        </Box>
      </Box>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">
            {dictionary.invoiceform.description}
          </Typography>
          <Controller
            name="description"
            control={control}
            rules={{ required: dictionary.errormessages.form_fieldrequired }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                fullWidth
                freeSolo
                options={[...previousDescriptions]}
                value={value || ""}
                inputValue={value || ""}
                onInputChange={(_, newInputValue) => {
                  onChange(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={dictionary.invoiceform.choosedescription}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            )}
          />
        </Box>
      </Box>
      <Box className="row">
        <Box className="column type-column">
          <Typography variant="h4">{dictionary.invoiceform.type}</Typography>
          <Controller
            name="type"
            control={control}
            rules={{ required: dictionary.errormessages.form_fieldrequired }}
            render={({ field }) => (
              <Select
                {...field}
                displayEmpty
                notched
                fullWidth
                error={!!errors.type}
              >
                <MenuItem value="">
                  <em>{dictionary.invoiceform.choosetype}</em>
                </MenuItem>
                <MenuItem value="inkomst">Inkomst</MenuItem>
                <MenuItem value="uitgave">Uitgave</MenuItem>
                <MenuItem value="varia">Varia</MenuItem>
                <MenuItem value="uitbetaling">Uitbetaling</MenuItem>
              </Select>
            )}
          />
          {errors.type && (
            <Typography variant="caption" color="error">
              {errors.type.message}
            </Typography>
          )}
        </Box>

        <Box className="column project-column">
          <Typography variant="h4">
            {dictionary.invoiceform.projectcode}
          </Typography>
          <Controller
            name="projectId"
            control={control}
            rules={{ required: dictionary.errormessages.form_fieldrequired }}
            render={({ field }) => {
              const selectedProject =
                projects?.find((p) => p.id === field.value) || null;

              return (
                <Autocomplete
                  options={projects || []}
                  getOptionLabel={(option) =>
                    `${option.code} - ${
                      option.contact &&
                      `${option.contact.address.street} ${option.contact.address.number} ${option.contact.address.city}`
                    }`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedProject}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id ?? null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      error={!!errors.projectId}
                      helperText={errors.projectId?.message}
                      placeholder={dictionary.invoiceform.chooseproject}
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          height: "56px", // Match Select height
                        },
                      }}
                    />
                  )}
                  sx={{
                    width: "100%",
                  }}
                />
              );
            }}
          />
        </Box>
      </Box>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">
            {dictionary.invoiceform.priceexvat}
          </Typography>
          <Controller
            name="priceExVat"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                fullWidth
                error={!!errors.priceExVat}
                helperText={errors.priceExVat?.message}
              />
            )}
          />
        </Box>
        <Box className="column">
          <Typography variant="h4">{dictionary.invoiceform.vat}</Typography>
          <Controller
            name="vat"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                fullWidth
                error={!!errors.vat}
                helperText={errors.vat?.message}
              />
            )}
          />
        </Box>
        <Box className="column">
          <Typography variant="h4">
            {dictionary.invoiceform.totalamount}
          </Typography>
          <TextField
            disabled
            type="number"
            fullWidth
            error={!!errors.priceExVat}
            helperText={errors?.priceExVat?.message}
            InputLabelProps={{ shrink: true }}
            value={totalAmount}
          />
        </Box>
      </Box>
      <Box className="row">
        <Button type="submit" sx={{ width: "100%" }}>
          {dictionary.buttons.savebtn}
        </Button>
      </Box>
    </StyledForm>
  );
};
